import { computed, Injectable, inject } from '@angular/core';
import {
  ExportType,
  StatusResponse,
} from '@app/modules/service-data/types/service-data.types';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { DOCUMENT_DATE_TIME_FORMAT } from '@app/app.constants';
import { BlobUriResponseModel, RuntimeStatus } from '@core/core.types';
import { ExportersService } from '@core/services/exporters.service';
import { ExportersUtilsService } from '@core/services/exporters-utils.service';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';
import { CoreUtilsService } from '@core/utils/core-utils.service';

@Injectable({
  providedIn: 'root',
})
export class LaboratoryBatchExportService {
  private exportersUtilsService = inject(ExportersUtilsService);
  private exportersService = inject(ExportersService);
  private datePipe = inject(DatePipe);
  private translate = inject(TranslateService);

  public export(
    batchId: string,
    type: LaboratoryBatchExportType
  ): Observable<string> {
    const fileName = `${type}_${this.datePipe.transform(
      new Date(),
      DOCUMENT_DATE_TIME_FORMAT
    )}.xlsx`;

    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translate.instant('laboratory.export.types.' + type)
    );

    return this.exportersService.getCreateBlobUri(fileName).pipe(
      switchMap((response: BlobUriResponseModel) =>
        this.exportersService.exportBatch(type, response.uri, batchId)
      ),
      catchError(() => of(RuntimeStatus.Failed)),
      switchMap((status: StatusResponse) =>
        this.exportersUtilsService.download(fileName, status.InstanceId)
      ),
      tap((status) => {
        if (status !== RuntimeStatus.Running) {
          progressSubject.next({ value: 100, status, success: true });
        }
      })
    );
  }
}

export enum LaboratoryBatchExportType {
  BatchSamples = 'BatchSamples',
  BatchIssues = 'BatchIssues',
  BatchLabels = 'BatchLabels',
}
